"use client";

import { motion } from "framer-motion";
import type { Locale } from "@/config/i18n";
import { CHAUFFEUR_SLUG_UI } from "./chauffeurSlugUi";
import { tr } from "@/config/localeText";

type ShowcaseCard = {
  title: string;
  text: string;
  tag?: string;
};

type ShowcaseGridProps = {
  heading: string;
  subheading?: string;
  items: ShowcaseCard[];
  variant?: "light" | "dark";
  locale?: Locale;
};

export const ShowcaseGrid = ({
  heading,
  subheading,
  items,
  variant = "light",
  locale = "fr",
}: ShowcaseGridProps): JSX.Element => {
  const ui = tr(CHAUFFEUR_SLUG_UI, locale);
  const isLight = variant === "light";
  const sectionBg = isLight ? "bg-white text-black" : "bg-black text-white";
  const borderCol = isLight ? "border-black/10" : "border-white/10";
  const cardBg = isLight ? "bg-white" : "bg-neutral-900/35";
  const subText = isLight ? "text-black/70" : "text-white/75";

  return (
    <section className={`py-16 ${sectionBg}`}>
      <div className="mx-auto max-w-[90rem] px-6 md:px-8">
        <div
          className={`mb-6 rounded-2xl border ${borderCol} ${isLight ? "bg-black/[0.02]" : "bg-white/[0.03]"} p-5`}
        >
          <p
            className={`mb-2 text-xs uppercase tracking-[0.18em] ${isLight ? "text-black/55" : "text-white/55"}`}
          >
            {ui.showcaseEyebrow}
          </p>
          <motion.h2
            initial={false}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45 }}
            viewport={{ once: true, margin: "-10%" }}
            className="mb-2 font-space-age text-xl tracking-[0.2em]"
          >
            {heading}
          </motion.h2>
          {subheading && <p className={subText}>{subheading}</p>}
        </div>
        <div className="grid items-start gap-4 lg:grid-cols-12">
          {items[0] && (
            <motion.div
              key={items[0].title}
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              viewport={{ once: true, margin: "-10%" }}
              className={`rounded-3xl border ${borderCol} ${cardBg} p-6 lg:col-span-7`}
            >
              {items[0].tag && (
                <span className="mb-2 inline-flex rounded-full border border-current px-2 py-0.5 text-[10px] uppercase tracking-[0.25em] opacity-70">
                  {items[0].tag}
                </span>
              )}
              <div className="mb-2 text-base font-semibold md:text-lg">
                {items[0].title}
              </div>
              <p className={`text-sm ${subText}`}>{items[0].text}</p>
            </motion.div>
          )}
          <div className="grid gap-4 sm:grid-cols-2 lg:col-span-5">
            {items.slice(1).map((it, idx) => (
              <motion.div
                key={it.title}
                initial={false}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: idx * 0.05 }}
                viewport={{ once: true, margin: "-10%" }}
                className={`rounded-2xl border ${borderCol} ${cardBg} p-5`}
              >
                {it.tag && (
                  <span className="mb-2 inline-flex rounded-full border border-current px-2 py-0.5 text-[10px] uppercase tracking-[0.25em] opacity-70">
                    {it.tag}
                  </span>
                )}
                <div className="mb-1 text-sm font-semibold">{it.title}</div>
                <p className={`text-sm ${subText}`}>{it.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ShowcaseGrid;
